// Season ground + flower dressing — the pure, GPU-free seams the render body composes
// (design D5, UI.md §8). A flat, season-tinted colour fill (`groundFillColorForTile`,
// field-ground-scatter — replacing the earlier tiled ground texture) sits behind the
// y-sorted draw list, its colour chosen by `MatchState.season`; the season transition
// crossfades within the 240 ms motion budget under the `full` animation mode and
// hard-swaps under `reduced`. World-space wayfinding landmarks (grass tufts, rocks) are a
// separate concern, in `ground-scatter.ts`. Each patch draws a live flower sheet when
// `PatchRecord.pool > 0` and the dead-flower sheet when it does not, with the live colour
// variety a deterministic hash of `patch.id` (variety is a client cosmetic — no wire field).
//
// Everything here is plain logic with no Pixi/GPU dependency and no import from
// `renderer.ts` (keeping the module graph one-directional: `renderer` → `season-ground`),
// so it is unit-tested in `renderer.test.ts` without a canvas. The draw-time texture use
// and the actual ground quad live in the group-11 draw body, exercised on-device (D-086).

import { Season } from '../net/wire/messages';
import type { PatchRecord } from '../net/wire/messages';
import type { AssetKey } from './assets';
import type { AnimationMode } from './animation-mode';

/** UI.md §2 motion budget — no fade runs longer than 240 ms. The season crossfade shares
 * the same ceiling as the tier crossfade (`renderer.ts` `MOTION_BUDGET_MS`). */
export const SEASON_CROSSFADE_BUDGET_MS = 240;

/** The ground tile texture key for a streamed season (design D5). Total over `Season`, so
 * a new season variant is a compile error here rather than a silent blank ground. */
export function groundTileForSeason(season: Season): AssetKey {
  switch (season) {
    case Season.Summer:
      return 'grassSummer';
    case Season.Autumn:
      return 'grassAutumn';
    case Season.Winter:
      return 'grassWinter';
  }
}

/** The five live flower colour variety sheets (design D5). The dead-flower sheet
 * (`flowerDead`) is never a variety — it is selected only when a patch is past bloom. */
const FLOWER_VARIETIES: readonly AssetKey[] = [
  'flowerBlue',
  'flowerPink',
  'flowerPurple',
  'flowerRed',
  'flowerWhite',
];

/** A 32-bit integer avalanche finalizer (MurmurHash3 `fmix32`) — mixes a patch id into a
 * well-distributed hash so `% FLOWER_VARIETIES.length` spreads across all five colours
 * rather than clustering on the id's low bits. `Math.imul` gives deterministic 32-bit
 * wraparound independent of the id's magnitude. */
function fmix32(value: number): number {
  let h = value >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = (h ^ (h >>> 16)) >>> 0;
  return h;
}

/** The live flower colour variety sheet for a patch, chosen deterministically from its
 * `id` (design D5): the same id always yields the same variety, independent of frame or
 * render order — variety is a pure client cosmetic, absent from the wire. */
export function flowerVarietyFor(patchId: number): AssetKey {
  return FLOWER_VARIETIES[fmix32(patchId) % FLOWER_VARIETIES.length];
}

/** The flower sheet a patch draws (design D5): the dead-flower sheet past bloom
 * (`pool === 0`), otherwise its deterministic live colour variety. `pool > 0` is exactly the
 * predicate the former `hasNectar` bool carried — the quantization floors any strictly-positive
 * pool at 1, so a nearly-drained patch is still a live flower. */
export function flowerSheetForPatch(patch: PatchRecord): AssetKey {
  return patch.pool > 0 ? flowerVarietyFor(patch.id) : 'flowerDead';
}

/** Flat season base-fill colour (design "flat season-tinted fill", field-ground-scatter):
 * replaces the old tiled `grass_summer/autumn/winter.png` ground texture with a single
 * colour per season, so the ground reads as a plain field rather than a visible repeating
 * tile — the wayfinding landmarks now live in `ground-scatter.ts`'s world-space scatter
 * instead. Each colour is the *dominant* opaque pixel colour of the corresponding tile
 * (counted directly from the PNG: `grass_summer.png` → `rgb(124,167,75)`, 873/1024 pixels;
 * `grass_autumn.png` → `rgb(192,145,63)`; `grass_winter.png` → `rgb(195,203,206)`), so the
 * flat fill keeps the same seasonal palette the tiled texture had — a deliberate visual
 * continuity, not a fresh colour choice. Keyed by the ground-tile `AssetKey` (not `Season`
 * directly) so it composes with `GroundLayer`'s existing current/old-tile model unchanged. */
const GROUND_FILL_COLOR: Partial<Record<AssetKey, number>> = {
  grassSummer: 0x7ca74b,
  grassAutumn: 0xc0913f,
  grassWinter: 0xc3cbce,
};

/** Defensive fallback for `groundFillColorForTile` — never reached in practice, since
 * `GroundLayer.current`/`.old` are always produced by `groundTileForSeason`, but `AssetKey`
 * itself is a broader union than the three ground tiles. */
const GROUND_FILL_FALLBACK = GROUND_FILL_COLOR.grassSummer as number;

/** The flat fill colour for a ground-tile asset key (design "flat season-tinted fill",
 * field-ground-scatter) — total over `AssetKey` (falls back to the summer colour rather
 * than throwing on an unrecognised key), even though the only keys it is ever called with
 * in practice are the three `groundTileForSeason` results. */
export function groundFillColorForTile(tile: AssetKey): number {
  return GROUND_FILL_COLOR[tile] ?? GROUND_FILL_FALLBACK;
}

/** The decision for a season change (design D5): the outgoing/incoming ground tiles and
 * whether to crossfade (`full` mode) or hard-swap (`reduced`/`none`). Pure — the timing
 * lives in `GroundLayer`. */
export interface SeasonTransition {
  /** The outgoing ground tile (the previous season's). */
  readonly from: AssetKey;
  /** The incoming ground tile (the next season's). */
  readonly to: AssetKey;
  /** True in `full` mode — retain and crossfade the outgoing tile within the motion
   * budget; false in `reduced`/`none` — hard-swap, dropping the outgoing tile the same
   * frame. */
  readonly crossfade: boolean;
}

/** Decides how to move the ground from `prev` to `next` under an animation mode (design
 * D5): crossfade only in `full` mode, hard-swap otherwise. Pure. */
export function seasonTransition(
  prev: Season,
  next: Season,
  mode: AnimationMode,
): SeasonTransition {
  return {
    from: groundTileForSeason(prev),
    to: groundTileForSeason(next),
    crossfade: mode === 'full',
  };
}

/**
 * The stateful per-season ground layer (design D5). Holds the current ground tile and, on
 * a season change, either crossfades from the outgoing tile within the 240 ms motion
 * budget (`full` mode) or hard-swaps to the incoming tile with no retention
 * (`reduced`/`none`). `update` is driven each frame with the streamed season, the current
 * animation mode, and the elapsed milliseconds since the previous frame (the RAF loop's
 * injected clock supplies the delta, group 10) — so the fade timing is deterministically
 * testable against a fake clock without a GPU. The draw body reads `current`/`old`/
 * `progress` to composite the two tiles.
 */
export class GroundLayer {
  private currentTile: AssetKey;
  private oldTile: AssetKey | null = null;
  private elapsedMs = 0;
  private season: Season;

  constructor(
    initialSeason: Season,
    private readonly motionBudgetMs = SEASON_CROSSFADE_BUDGET_MS,
  ) {
    this.season = initialSeason;
    this.currentTile = groundTileForSeason(initialSeason);
  }

  /** The incoming (front) ground tile. */
  get current(): AssetKey {
    return this.currentTile;
  }

  /** The outgoing tile retained during a `full`-mode crossfade, or null when no fade is in
   * flight (idle, or after a `reduced` hard-swap). */
  get old(): AssetKey | null {
    return this.oldTile;
  }

  /** Crossfade progress in [0, 1]; 1 once the motion budget is spent (the old tile then
   * dropped). */
  get progress(): number {
    return Math.min(1, this.elapsedMs / this.motionBudgetMs);
  }

  /** Whether an outgoing tile is still being cross-faded. */
  isCrossfading(): boolean {
    return this.oldTile !== null;
  }

  /**
   * Reconciles the ground layer to `season` under `mode`, advancing any in-flight
   * crossfade by `elapsedMs`. A season change decides crossfade vs hard-swap via
   * `seasonTransition`; an unchanged season just advances the fade.
   */
  update(season: Season, mode: AnimationMode, elapsedMs: number): void {
    if (season !== this.season) {
      const decision = seasonTransition(this.season, season, mode);
      if (decision.crossfade) {
        this.oldTile = this.currentTile; // retain the outgoing tile for the fade
        this.elapsedMs = 0;
      } else {
        this.oldTile = null; // hard-swap: drop any retained tile the same frame
      }
      this.currentTile = decision.to;
      this.season = season;
    }
    this.advance(elapsedMs);
  }

  private advance(elapsedMs: number): void {
    if (this.oldTile === null) {
      return; // nothing in flight
    }
    this.elapsedMs += elapsedMs;
    if (this.elapsedMs >= this.motionBudgetMs) {
      this.elapsedMs = this.motionBudgetMs;
      this.oldTile = null; // motion budget spent: drop the retained outgoing tile
    }
  }
}
